import { t } from 'i18next';
import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { SxProp, View } from 'dripsy';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { UniTextDetailsCard } from '@/design-system/components/cards';
import { UniCheckbox } from '@/design-system/components/inputs';
import { ModalHeader } from '@/design-system/components/modal';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { useStore } from 'store/store';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { RedeemStackRoutes, RedeemStackScreenProps } from './types';
import { CurrenciesContext } from '@/currencies/currency.context';
import { ExecuteRedemptionResponse, useExecuteRedemption } from './useExecuteRedemption.hook';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { CurrenciesIcon } from '../../components';
import BigNumber from 'bignumber.js';
import moment from 'moment-timezone';

export const RedeemConfirm: React.FC<RedeemStackScreenProps<RedeemStackRoutes.Confirm>> = ({ navigation, route }) => {
  const { subscription, netRedemptionAmount, isMax, onUpdate, onLayout, refreshPortfolio } = route.params;
  const layout = useContext(LayoutContext);
  const bottomSheetRef = useRef<BottomSheet>(null);

  const executeRedemption = useExecuteRedemption({
    assetSubscribedId: subscription.id,
    redemptionAmount: netRedemptionAmount,
    isFlexible: subscription.maturityDate === null,
    isMax
  });

  const [isAcknowledged, setIsAcknowledged] = useState(false);

  useEffect(() => {
    if (!executeRedemption.isSuccess) return;
    refreshPortfolio && refreshPortfolio();
    navigation.navigate(RedeemStackRoutes.Result, {
      ...route.params,
      resultData: executeRedemption.data?.data as ExecuteRedemptionResponse
    });
  }, [executeRedemption.isSuccess]);

  if (layout.isMobile) {
    // Get dimensions
    const renderBackdrop = useCallback(
      (props) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />
      ),
      []
    );
    const { width, height } = layout.dimensions;
    const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
      'CONTENT_HEIGHT',
      'CONTENT_HEIGHT'
    ]);
    // Bottom Sheet on mobile
    return (
      <View sx={{ width, height, backgroundColor: 'transparent' }}>
        <BottomSheet
          index={0}
          ref={bottomSheetRef}
          snapPoints={animatedSnapPoints}
          handleHeight={animatedHandleHeight}
          contentHeight={animatedContentHeight}
          backdropComponent={renderBackdrop}
          handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
          <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <BottomSheetHeader
              title={t('earn-invest::subscription-confirmation.header--subscription-confirmation')}
              onPress={() => {
                bottomSheetRef.current?.close();
                setTimeout(() => {
                  navigation.navigate(RedeemStackRoutes.Main, { ...route.params, goBack: true });
                }, 300);
              }}
              sx={{ paddingX: '$4', pb: 0 }}
            />
            <View sx={{ padding: '$4', gap: '$4' }}>
              <Body
                subscription={subscription}
                netRedemptionAmount={netRedemptionAmount}
                isAcknowledged={isAcknowledged}
                onChangeAcknowledged={(value) => setIsAcknowledged(value)}
                loading={executeRedemption.isLoading}
                error={executeRedemption.isError}
              />
            </View>
            <UniButton
              size="default"
              onPress={() => executeRedemption.redeem()}
              label={t('earn-invest::glossary.place-instruction')}
              wrapperSx={{ flex: 1, marginX: '$4', mb: '$4' }}
              disabled={!isAcknowledged}
              loading={false}
            />
          </View>
        </BottomSheet>
      </View>
    );
  }

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={onLayout}>
      <Header
        title={t('earn-invest::redeem-confirmation.header--redeem-confirmation')}
        isMobile={layout.isMobile}
        onClose={() => {
          // close
          navigation.navigate(RedeemStackRoutes.Main, { ...route.params, goBack: true });
        }}
      />
      <View sx={{ padding: '$7', gap: '$7' }}>
        {/* {executeSubsciption.errorText ? <UniAlert type={AlertType.Error} message={executeSubsciption.errorText} /> : null} */}
        <Body
          subscription={subscription}
          netRedemptionAmount={netRedemptionAmount}
          isAcknowledged={isAcknowledged}
          onChangeAcknowledged={(value) => setIsAcknowledged(value)}
          loading={executeRedemption.isLoading}
          error={executeRedemption.isError}
        />
      </View>
      <View sx={{ paddingX: '$7', pb: '$7', flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
        <UniButton
          size="default"
          variant={ButtonVariant.outline1}
          onPress={() => navigation.navigate(RedeemStackRoutes.Main, { ...route.params, goBack: true })}
          label={t('earn-invest::glossary.back')}
          wrapperSx={{ flex: 1 }}
        />
        <UniButton
          size="default"
          onPress={() => executeRedemption.redeem()}
          label={t('earn-invest::glossary.place-instruction')}
          wrapperSx={{ flex: 1 }}
          disabled={!isAcknowledged}
          loading={false}
        />
      </View>
    </View>
  );
};

const Header = ({ title, isMobile, onClose }: { title: string; isMobile: boolean; onClose: () => void }) => {
  if (isMobile) {
  }

  return <ModalHeader title={title} closeModal={onClose} />;
};

const Body = ({
  subscription,
  netRedemptionAmount,
  isAcknowledged,
  loading,
  onChangeAcknowledged,
  error
}: {
  subscription: UserEarnActionListResponseDataItem;
  netRedemptionAmount: string;
  isAcknowledged: boolean;
  loading: boolean;
  onChangeAcknowledged: (value: boolean) => void;
  error?: boolean;
}) => {
  const store = useStore();
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const isLocked = subscription.maturityDate !== null;

  const earlyRedeem = isLocked && moment().isBefore(moment(subscription.maturityDate));

  let amountDetails = [
    {
      name: isLocked ? t('earn-invest::glossary.forfeited-interest') : t('earn-invest::glossary.redemption-amount'),
      data: formatAmountByDecimals({
        value: isLocked ? subscription.rewardsAccrued : netRedemptionAmount,
        decimals: getDecimalByAsset(subscription.asset),
        suffix: subscription.asset
      })
    }
  ];

  return (
    <View sx={{ gap: '$5' }}>
      {earlyRedeem && <UniAlert type={AlertType.Warning} message={t('earn-invest::glossary.subscription-note-early-redemption')} />}
      <CurrenciesIcon asset={subscription.asset} title={subscription.offerName} />
      {isLocked && (
        <AmountSummary
          label={t('earn-invest::glossary.proceeds')}
          value={subscription.amount}
          asset={subscription.asset}
          loading={loading}
        />
      )}
      {!isLocked && (
        <AmountSummary
          label={t('earn-invest::glossary.net-amount')}
          value={subscription.amount}
          asset={subscription.asset}
          loading={loading}
        />
      )}
      <UniTextDetailsCard data={amountDetails} loading={loading} />
      <UniCheckbox
        label={t('earn-invest::redeem-confirmation.text--redeem-statement', { vendor: store.brand })}
        value={isAcknowledged}
        onValueChange={onChangeAcknowledged}
        options={{
          labelSecondaryColor: true
        }}
        disabled={loading}
      />
      {error && <UniAlert type={AlertType.Error} message={t('earn-invest::glossary.error-unable-to-redeem-subscribed-assets')} sx={{}} />}
    </View>
  );
};

enum StyleName {
  bottomsheetHeadHandler = 'bottomsheetHeadHandler'
}

const styles: Record<StyleName, SxProp> = {
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  }
};
